import { useState } from "react";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { requestAccessCsv } from "@/lib/utils";
import { validateEmail } from "@/lib/emailValidation";

interface WaitlistCTAProps {
  source?: string;
  buttonText?: string;
  buttonVariant?: "accent" | "default" | "hero";
  layout?: "inline" | "stacked";
  successContent?: React.ReactNode;
  className?: string;
}

export const WaitlistCTA = ({
  source = "Website",
  buttonText = "Join Waitlist",
  buttonVariant = "accent",
  layout = "inline",
  successContent,
  className = "",
}: WaitlistCTAProps) => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showEmailInput, setShowEmailInput] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (isSubmitting) return;

    const validation = validateEmail(email);
    if (!validation.valid) {
      toast({
        variant: "destructive",
        title: "Invalid email",
        description: validation.message,
        duration: 5000,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const response: any = await requestAccessCsv(email, source);
      if (response && response?.status && response?.status >= 400) {
        throw new Error(response.statusText);
      }
      setIsSubmitted(true);
      toast({
        title: "🎉 You're on the list!",
        description: "Welcome aboard! Your bugs don't stand a chance.",
        duration: 5000,
      });
    } catch (err) {
      console.error(err);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please refresh the page and try again.",
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className={className}>
        {successContent || (
          <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-accent/10 border border-accent/30 max-w-md mx-auto animate-fade-in">
            <CheckCircle2 className="w-6 h-6 text-accent flex-shrink-0" />
            <div className="text-left">
              <p className="font-medium text-foreground text-sm">You're in! 🚀</p>
              <p className="text-xs text-muted-foreground">We'll reach out when it's your turn.</p>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (!showEmailInput) {
    return (
      <div className={className}>
        <Button
          variant={buttonVariant}
          size="lg"
          className="h-12 px-8 hover:scale-105 transition-transform shadow-accent"
          onClick={() => setShowEmailInput(true)}
        >
          {buttonText}
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    );
  }

  return (
    <div className={`animate-fade-in ${className}`}>
      <form
        onSubmit={handleSubmit}
        className={
          layout === "inline"
            ? "flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
            : "max-w-md mx-auto space-y-3"
        }
      >
        <Input
          type="email"
          placeholder="Enter your email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="h-12 flex-1 bg-background border-2 border-accent/60 focus:border-accent text-foreground placeholder:text-foreground/60 text-base shadow-sm ring-accent/20 focus:ring-2 focus:ring-accent/30 transition-all"
          disabled={isSubmitting}
          autoFocus
        />
        <Button
          type="submit"
          variant={buttonVariant}
          size="lg"
          className={`h-12 px-8 hover:scale-105 transition-transform ${layout === "stacked" ? "w-full" : ""}`}
          disabled={isSubmitting}
        >
          {isSubmitting ? "Joining..." : "Submit"}
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </form>
    </div>
  );
};
